// Compare Section — compare-section.js
// Handles slot population, modal, product search, and keyPoints parsing.

(function () {
  'use strict';

  // ─── State ───────────────────────────────────────────────────────────────────

  let productData = null;
  let activeSlot = null;

  // ─── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    const dataEl = document.getElementById('compare-products-data');
    if (!dataEl) return;

    try {
      productData = JSON.parse(dataEl.textContent);
    } catch (_) {
      return;
    }

    const currentSpecsEl = document.querySelector('.compare-slot--current .compare-card__specs');
    fillParsedSpecs(currentSpecsEl, productData.currentProduct.keyPoints);

    autoLoadSlots(productData.currentProduct);
    bindEvents();
    observeSlots();
  }

  // ─── Key Points Parser ────────────────────────────────────────────────────────

  /**
   * Parses the charter_top_key_points multiline text into structured values.
   * Case-insensitive. Handles both "Key: value" and "Key value" formats.
   *
   * @param {string} text
   * @returns {{ fireProtectionRating: string, lockType: string, lockingBars: string, gunCapacity: string }}
   */
  function parseKeyPoints(text) {
    const result = {
      fireProtectionRating: '',
      lockType: '',
      lockingBars: '',
      gunCapacity: '',
    };

    if (!text || typeof text !== 'string') return result;

    const keyMap = [
      { keys: ['fire protection rating'], out: 'fireProtectionRating' },
      { keys: ['lock type'], out: 'lockType' },
      { keys: ['locking bars', 'bolt system'], out: 'lockingBars' },
      { keys: ['maximum gun capacity', 'gun capacity'], out: 'gunCapacity' },
    ];

    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

    for (const line of lines) {
      const lower = line.toLowerCase();
      for (const { keys, out } of keyMap) {
        if (result[out]) continue;
        for (const key of keys) {
          if (lower.includes(key)) {
            const colonIdx = line.indexOf(':');
            result[out] = colonIdx !== -1
              ? line.slice(colonIdx + 1).trim()
              : line.trim();
            break;
          }
        }
      }
    }

    return result;
  }

  /**
   * Fills [data-parsed-key] spec rows on a specs element using parsed keyPoints.
   * Shows rows with values, hides rows without.
   */
  function fillParsedSpecs(specsEl, keyPointsText) {
    if (!specsEl) return;
    const parsed = parseKeyPoints(keyPointsText);

    specsEl.querySelectorAll('[data-parsed-key]').forEach(row => {
      const value = parsed[row.dataset.parsedKey] || '';
      const valueEl = row.querySelector('.compare-spec__value');
      if (value && valueEl) {
        valueEl.textContent = value;
        row.hidden = false;
      } else {
        row.hidden = true;
      }
    });
  }

  // ─── Slot Auto-Population ─────────────────────────────────────────────────────

  function autoLoadSlots(current) {
    if (!productData?.allProducts?.length) return;

    const currentLevel = toNumber(current.securityLevel);
    const currentHasGuns = hasGunCapacity(current);

    const lowerProduct =
      findBySecurityLevel(currentLevel - 1, currentHasGuns) ||
      findBySecurityLevel(currentLevel - 2, currentHasGuns) ||
      findBySecurityLevel(currentLevel - 1, null);

    const higherProduct =
      findBySecurityLevel(currentLevel + 1, currentHasGuns) ||
      findBySecurityLevel(currentLevel + 2, currentHasGuns) ||
      findBySecurityLevel(currentLevel + 1, null);

    if (lowerProduct) loadSlot('left', lowerProduct);
    if (higherProduct) loadSlot('right', higherProduct);
  }

  function findBySecurityLevel(targetLevel, requireGuns) {
    if (!productData?.allProducts) return null;
    const currentId = productData.currentProduct?.id;

    return productData.allProducts.find(p => {
      if (p.id === currentId) return false;
      if (toNumber(p.securityLevel) !== targetLevel) return false;
      if (requireGuns !== null && hasGunCapacity(p) !== requireGuns) return false;
      return true;
    }) || null;
  }

  function hasGunCapacity(product) {
    return Boolean(parseKeyPoints(product.keyPoints || '').gunCapacity);
  }

  // ─── Slot Rendering ───────────────────────────────────────────────────────────

  function loadSlot(slot, product) {
    const emptyEl  = document.getElementById(`compareEmpty${cap(slot)}`);
    const productEl = document.getElementById(`compareProduct${cap(slot)}`);
    if (!emptyEl || !productEl) return;

    productEl.innerHTML = buildCardHTML(product, slot);
    emptyEl.hidden = true;
    productEl.hidden = false;
  }

  function clearSlot(slot) {
    const emptyEl  = document.getElementById(`compareEmpty${cap(slot)}`);
    const productEl = document.getElementById(`compareProduct${cap(slot)}`);
    if (!emptyEl || !productEl) return;

    productEl.innerHTML = '';
    productEl.hidden = true;
    emptyEl.hidden = false;
  }

  function buildCardHTML(product, slot) {
    const parsed = parseKeyPoints(product.keyPoints || '');
    const isOnSale = product.compareAtPrice && product.compareAtPrice > product.price;

    const specs = [
      { icon: iconTag(),       label: 'Brand',             value: product.brand },
      { icon: iconBriefcase(), label: 'Model',             value: product.model },
      { icon: iconShield(),    label: 'Protection Level',  value: product.protectionLevel },
      { icon: iconLock(),      label: 'Security Level',    value: product.securityLevel ? `${product.securityLevel} / 10` : '' },
      { icon: iconFire(),      label: 'Fire Protection',   value: product.fireProtectionLevel },
      { icon: iconFire(),      label: 'Fire Rating',       value: parsed.fireProtectionRating },
      { icon: iconKey(),       label: 'Lock Type',         value: parsed.lockType },
      { icon: iconList(),      label: 'Locking Bars',      value: parsed.lockingBars },
      { icon: iconList(),      label: 'Gun Capacity',      value: parsed.gunCapacity },
      { icon: iconGlobe(),     label: 'Country of Origin', value: product.countryOfOrigin },
    ];

    const specsHTML = specs
      .filter(s => s.value)
      .map(s => `
        <div class="compare-spec">
          <dt class="compare-spec__label">${s.icon}${esc(s.label)}</dt>
          <dd class="compare-spec__value">${esc(s.value)}</dd>
        </div>
      `).join('');

    const badgeHTML = isOnSale
      ? '<span class="compare-card__badge compare-card__badge--sale">On Sale</span>'
      : '';

    const name = product.brand
      ? (product.model ? `${esc(product.brand)} &mdash; ${esc(product.model)}` : esc(product.brand))
      : esc(product.title);

    return `
      <article class="compare-card">
        <button
          class="compare-card__remove"
          data-remove-slot="${slot}"
          aria-label="Remove this safe from comparison"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>

        <div class="compare-card__badge-row">${badgeHTML}</div>

        <a href="${product.url}" class="compare-card__image-wrap" tabindex="-1" aria-hidden="true">
          <img
            src="${product.image}"
            alt="${esc(product.title)}"
            loading="lazy"
            class="compare-card__image"
          >
        </a>

        <div class="compare-card__info">
          <h3 class="compare-card__name">
            <a href="${product.url}" class="compare-card__name-link">${name}</a>
          </h3>
          <div class="compare-card__price">
            ${isOnSale ? `<s class="compare-card__price--was">${money(product.compareAtPrice)}</s>` : ''}
            <span class="compare-card__price--now">${money(product.price)}</span>
          </div>
        </div>

        <dl class="compare-card__specs">
          ${specsHTML}
        </dl>

        <div class="compare-card__actions">
          <a href="${product.url}" class="compare-card__cta">View This Safe</a>
        </div>
      </article>
    `;
  }

  // ─── Modal ────────────────────────────────────────────────────────────────────

  function openModal(slot) {
    activeSlot = slot;
    const modal = document.getElementById('compareModal');
    if (!modal || !productData?.allProducts) return;

    renderModalGrid(productData.allProducts);
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    const search = document.getElementById('compareModalSearch');
    if (search) {
      search.value = '';
      search.focus();
    }
  }

  function closeModal() {
    const modal = document.getElementById('compareModal');
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
    activeSlot = null;
  }

  function renderModalGrid(products) {
    const grid = document.getElementById('compareModalGrid');
    if (!grid) return;

    const currentId = productData?.currentProduct?.id;
    const filtered = products.filter(p => p.id !== currentId);

    grid.innerHTML = filtered.map(p => {
      const name = p.brand
        ? (p.model ? `${esc(p.brand)} &mdash; ${esc(p.model)}` : esc(p.brand))
        : esc(p.title);

      return `
        <button
          class="compare-modal__product"
          role="listitem"
          data-product-id="${p.id}"
          aria-label="Select ${esc(p.title)} for comparison"
        >
          <img src="${p.image}" alt="" loading="lazy" class="compare-modal__product-image">
          <span class="compare-modal__product-name">${name}</span>
          <span class="compare-modal__product-price">${money(p.price)}</span>
        </button>
      `;
    }).join('');
  }

  function filterModal(query) {
    const grid = document.getElementById('compareModalGrid');
    if (!grid) return;
    const q = query.toLowerCase().trim();
    grid.querySelectorAll('.compare-modal__product').forEach(btn => {
      btn.hidden = Boolean(q) && !btn.textContent.toLowerCase().includes(q);
    });
  }

  // ─── Events ───────────────────────────────────────────────────────────────────

  function bindEvents() {
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeydown);

    const search = document.getElementById('compareModalSearch');
    if (search) {
      search.addEventListener('input', () => filterModal(search.value));
    }
  }

  function handleClick(e) {
    // Open slot modal
    const addBtn = e.target.closest('.compare-slot__add[data-slot]');
    if (addBtn) {
      openModal(addBtn.dataset.slot);
      return;
    }

    // Select product from modal
    const productBtn = e.target.closest('.compare-modal__product');
    if (productBtn && activeSlot) {
      const id = Number(productBtn.dataset.productId);
      const product = productData.allProducts.find(p => p.id === id);
      if (product) {
        loadSlot(activeSlot, product);
        closeModal();
      }
      return;
    }

    // Remove product from slot
    const removeBtn = e.target.closest('[data-remove-slot]');
    if (removeBtn) {
      clearSlot(removeBtn.dataset.removeSlot);
      return;
    }

    // Close modal via backdrop or close button
    if (
      e.target.id === 'compareModalBackdrop' ||
      e.target.id === 'compareModalClose' ||
      e.target.closest('#compareModalClose')
    ) {
      closeModal();
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      const modal = document.getElementById('compareModal');
      if (modal && !modal.hidden) closeModal();
    }
  }

  // ─── Scroll Animations ────────────────────────────────────────────────────────

  function observeSlots() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.compare-slot').forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.compare-slot').forEach(el => observer.observe(el));
  }

  // ─── Inline SVG Icons ─────────────────────────────────────────────────────────

  function svg(path) {
    return `<svg class="compare-spec__icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
  }

  function iconTag()       { return svg('<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>'); }
  function iconBriefcase() { return svg('<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>'); }
  function iconShield()    { return svg('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>'); }
  function iconLock()      { return svg('<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>'); }
  function iconFire()      { return svg('<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>'); }
  function iconKey()       { return svg('<path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>'); }
  function iconList()      { return svg('<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>'); }
  function iconGlobe()     { return svg('<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>'); }

  // ─── Utilities ────────────────────────────────────────────────────────────────

  function cap(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function toNumber(val) {
    const n = parseInt(val, 10);
    return isNaN(n) ? 0 : n;
  }

  function money(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  function esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── Shopify Theme Editor ─────────────────────────────────────────────────────

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);

  // ─── Boot ─────────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
